// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import React, { useState, useCallback, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Portal from "@material-ui/core/Portal";
import Snackbar from "@material-ui/core/Snackbar";

import ApproveTable from "./ApproveTable";
import BusinessUnitFilter from "./BusinessUnitFilter";
import GlobalSearch from "./GlobalSearch";
import Alert from "../Alert";
import BackdropProgress from "../BackdropProgress";
import {
  DEFAULT_TABLE_PAGE_SIZE,
  DEFAULT_TABLE_OFFSET,
  ENGAGEMENT_TABLE_COLUMN_FIELD_MAP,
  SORT_ORDER,
} from "../utils/AppConstants";
import useHttp from "../utils/http";
import { getDateFromString, formatDateWithDashes } from "../utils/utils";
import { AppConfig, FormConstants } from "../../Config";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(1),
    textAlign: "center",
  },

  formControl: {
    margin: theme.spacing(1),
    minWidth: 100,
    width: "90%",
  },
  page: {
    padding: 20,
    flexGrow: 1,
    boxShadow: "0px 3px 12px #00000017",
  },
}));

const ApproveEngagementView = ({ isInternalEngagement }) => {
  const [isMessageBarOpen, setIsMessageBarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertMessageType, setAlertMessageType] = useState("error");
  const [checkboxStateBU, setCheckboxStateBU] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [sortBy, setSortBy] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [tablePageSize, setTablePageSize] = useState(DEFAULT_TABLE_PAGE_SIZE);
  const [isResetPageIndex, setIsResetPageIndex] = useState(false);
  const [data, setData] = useState([]);
  const classes = useStyles();
  const { handleRequest } = useHttp();

  const today = new Date();
  const initialSearchFiltersState = {
    type: null,
    account: "",
    allocationType: "",
    engagementStatus: "",
    requestStatus: "",
    startDate: new Date(
      today.getFullYear() - 2,
      today.getMonth(),
      today.getDate()
    ),
    endDate: null,
  };
  const [searchFilters] = useState(initialSearchFiltersState);

  useEffect(() => {
    setFilteredData(data);
  }, [data]);

  const handleSearch = (filterText) => {
    const lowercasedFilter = filterText.toLowerCase();
    const filteredData = data.filter((item) => {
      return Object.keys(item).some((key) =>
        item[key].toString().toLowerCase().includes(lowercasedFilter)
      );
    });
    setFilteredData(filteredData);
  };

  const getEngagementData = useCallback(
    async (limit, offset, searchString, sortByObj) => {
      if (validateFormFields(searchFilters)) {
        let engagements = [];
        const endPointUrl =
          AppConfig.baseUrl + AppConfig.getEngagementsforApproval;

        let filterBUList = checkboxStateBU
          .filter((checkbox) => checkbox.isChecked)
          .map((trueCheckbox) => trueCheckbox.name);

        let engagementCodeSearchStringList = [];
        let engagementCodeExcludeStringList = [];

        let body = {
          type: FormConstants.Types.Internal,
          engagementType: isInternalEngagement
            ? FormConstants.EngagementTypes.InternalEngagement.id
            : FormConstants.EngagementTypes.MarketingEvent.id,
          account: searchFilters.account?.customerName,
          allocationType: searchFilters.allocationType?.allocationTypeName,
          engagementStatus: searchFilters.engagementStatus,
          requestStatus: searchFilters.requestStatus,
          startDate: searchFilters.startDate
            ? formatDateWithDashes(searchFilters.startDate)
            : "",
          endDate: searchFilters.endDate
            ? formatDateWithDashes(searchFilters.endDate)
            : "",
          businessUnits: filterBUList,
          engagementCodeSearchStrings: engagementCodeSearchStringList,
          engagementCodeExcludeStrings: engagementCodeExcludeStringList,

          limit: limit ?? tablePageSize,
          offset: offset ?? DEFAULT_TABLE_OFFSET,

          searchString: searchString ?? filterText,
          searchColumns: [
            "id",
            "customerName",
            "opportunityName",
            "engagementCode",
            "allocationType",
            "engagementNature",
            "country",
            "startDate",
            "endDate",
            "requestStatus",
            "engagementStatus",
            "allocationConsultant",
            "allocationStartDate",
            "allocationEndDate",
          ],

          sortColumn: sortByObj
            ? ENGAGEMENT_TABLE_COLUMN_FIELD_MAP[sortByObj.id]
            : sortBy
              ? ENGAGEMENT_TABLE_COLUMN_FIELD_MAP[sortBy.id]
              : null,
          sortOrder: sortByObj
            ? sortByObj.desc
              ? SORT_ORDER.DESC
              : SORT_ORDER.ASC
            : sortBy
              ? sortBy.desc
                ? SORT_ORDER.DESC
                : SORT_ORDER.ASC
              : null,
        };

        handleRequest(
          endPointUrl,
          "POST",
          body,
          (data) => {
            data.forEach((item, index) => {
              let consultantsList = [];
              item.consultants.forEach((consultant) => {
                consultantsList.push(
                  `${consultant.email} (${getDateFromString(consultant.allocationStartDate)} - ${getDateFromString(consultant.allocationEndDate)})`
                );
              });
              engagements.push({
                engagementId: item.id,
                type: item.type,
                customerName: item.customerName,
                opportunityName: item.opportunityName,
                engagementCode: item.engagementCode,
                allocationTypeName: item.allocationTypeName,
                engagementNature: item.engagementNature,
                country: item.country,
                startDate: item.startDate && getDateFromString(item.startDate),
                endDate: item.endDate && getDateFromString(item.endDate),
                action: "",
                consultants: consultantsList,
                requestStatus: item.requestStatus,
                engagementStatus: item.engagementStatus,
                productName: item.productName,
                address: item.address,
                customerContact: item.customerContact,
                businessDomain: item.businessDomain,
                opportunityOwner: item.opportunityOwner,
                technicalOwner: item.technicalOwner,
                isContractsSigned: item.isContractsSigned,
                engagementScope: item.engagementScope,
                comment: item.comment,
                username: item.username,
              });
            });

            setData(engagements);
          },
          () => {
            setMessageBar("An error occurred in searching", true, "error");
            setData([]);
          },
          setIsDataLoading
        );
      } else {
        setMessageBar("There are errors in the search fields.", true, "error");
      }
    },
    [
      isInternalEngagement,
      tablePageSize,
      filterText,
      checkboxStateBU,
      sortBy,
      searchFilters,
    ]
  );

  const validateFormFields = (searchFilters) => {
    return !(isNaN(searchFilters.startDate) || isNaN(searchFilters.endDate));
  };

  const getBusinessUnits = useCallback(async () => {
    let buCheckBoxList = [];
    const endPointUrl = AppConfig.baseUrl + AppConfig.getBusinessUnitsList;

    handleRequest(endPointUrl, "GET", null, (data) => {
      data.forEach((item) => {
        buCheckBoxList.push({
          id: item.id,
          label: item.isActive ? item.name : `${item.name} (Deprecated)`,
          name: item.name,
          isChecked: false,
        });
      });

      setCheckboxStateBU(buCheckBoxList);
    });
  });

  useEffect(() => {
    getBusinessUnits();
  }, []);

  const fetchDataOnSearch = () => {
    setIsResetPageIndex((prev) => !prev);
    getEngagementData();
  };

  const fetchData = (pageSize, pageIndex, filterText, sortBy) => {
    let sortByObj =
      sortBy instanceof Array ? (sortBy.length > 0 ? sortBy[0] : null) : sortBy;
    getEngagementData(pageSize, pageSize * pageIndex, filterText, sortByObj);
  };

  const setMessageBar = (message, isOpen, type) => {
    setAlertMessage(message);
    setIsMessageBarOpen(isOpen);
    setAlertMessageType(type);
  };

  const handleMessageBarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setIsMessageBarOpen(false);
    setAlertMessage("");
  };

  return (
    <React.Fragment>
      <Portal>
        <Snackbar
          open={isMessageBarOpen}
          autoHideDuration={6000}
          onClose={handleMessageBarClose}
        >
          <Alert onClose={handleMessageBarClose} severity={alertMessageType}>
            {alertMessage}
          </Alert>
        </Snackbar>
      </Portal>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Typography variant="h5" display="block" gutterBottom>
            {isInternalEngagement
              ? "Approve Internal Engagements"
              : "Approve Marketing Events"}
          </Typography>
        </Grid>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <GlobalSearch onSearch={handleSearch} />
          </Grid>
          <Grid item xs={6}>
            <BusinessUnitFilter
              showHeader={false}
              checkBoxes={checkboxStateBU}
              setCheckboxesState={setCheckboxStateBU}
              fetchData={fetchDataOnSearch}
              showAllFilters={false}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <ApproveTable
          data={filteredData}
          isLoading={isDataLoading}
          isInternalEngagement={isInternalEngagement}
          setTablePageSize={setTablePageSize}
          fetchData={fetchData}
          isResetPageIndex={isResetPageIndex}
          setFilterText={setFilterText}
          setSortBy={setSortBy}
        />
      </Grid>

      <BackdropProgress open={isLoading} />
    </React.Fragment>
  );
};

export default ApproveEngagementView;
